import React, { useState, useEffect } from 'react';
import {
  Box,
  Avatar,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Button,
  Chip,
  useTheme,
  alpha,
  Tooltip,
} from '@mui/material';
import {
  AccountCircle as AccountIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  Upgrade as UpgradeIcon,
  Person as PersonIcon,
  Keyboard as KeyboardIcon,
  Help as HelpIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { useAppSelector } from '../store';
import { ENDPOINTS } from '../config/api';

interface BottomSectionProps {
  onOpenSettings?: () => void;
  onLogout?: () => void;
  onViewPlans?: () => void;
  onProfileMenu?: () => void;
}

function getJwtFromCookie() {
  const match = document.cookie.match(/(?:^|; )jwt=([^;]*)/);
  return match ? decodeURIComponent(match[1]) : null;
}

// Utility to fetch user info from backend
async function fetchUserData() {
  const jwt = getJwtFromCookie();
  if (!jwt) return null;
  try {
    const response = await fetch(ENDPOINTS.USERS_ME, {
      headers: {
        'Authorization': `Bearer ${jwt}`,
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });
    if (response.ok) {
      const data = await response.json();
      return data.data || data;
    }
  } catch (error) {
    console.error('Failed to fetch user data:', error);
  }
  return null;
}

const BottomSection: React.FC<BottomSectionProps> = ({
  onOpenSettings,
  onLogout,
  onViewPlans,
  onProfileMenu,
}) => {
  const theme = useTheme();
  const { isCollapsed } = useAppSelector((state) => state.sidebar);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [user, setUser] = useState<any>(null);
  const [loadingUser, setLoadingUser] = useState(true);

  useEffect(() => {
    const loadUser = async () => {
      setLoadingUser(true);
      const userData = await fetchUserData();
      setUser(userData);
      setLoadingUser(false);
    };
    loadUser();
  }, []);

  const handleProfileClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleSettings = () => {
    onOpenSettings?.();
    handleCloseMenu();
  };

  const handleLogout = () => {
    onLogout?.();
    handleCloseMenu();
  };

  const handleViewPlans = () => {
    onViewPlans?.();
  };

  const getPlanColor = () => {
    switch (user?.plan) {
      case 'pro':
        return 'primary';
      case 'enterprise':
        return 'secondary';
      default:
        return 'default';
    }
  };

  const getPlanLabel = () => {
    switch (user?.plan) {
      case 'pro':
        return 'Pro';
      case 'enterprise':
        return 'Enterprise';
      default:
        return 'Free';
    }
  };

  // Collapsed state - show only avatar
  if (isCollapsed) {
    return (
      <Box
        sx={{
          p: 1,
          borderTop: `1px solid ${theme.palette.divider}`,
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <Tooltip
          title={
            loadingUser || !user ? (
              <Typography variant="body2">Loading...</Typography>
            ) : (
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                  {user.name || user.email}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {user.email}
                </Typography>
              </Box>
            )
          }
          placement="right"
        >
          <IconButton
            onClick={handleProfileClick}
            sx={{
              width: 40,
              height: 40,
              p: 0,
            }}
          >
            {loadingUser || !user ? (
              <Avatar sx={{ width: 32, height: 32, bgcolor: 'grey.400' }} />
            ) : user.picture ? (
              <Avatar src={user.picture} alt={user.name || user.email} sx={{ width: 32, height: 32 }} />
            ) : (
              <Avatar alt={user.name || user.email} sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                {(user.name || user.email)[0].toUpperCase()}
              </Avatar>
            )}
          </IconButton>
        </Tooltip>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        borderTop: `1px solid ${theme.palette.divider}`,
        backgroundColor: alpha(theme.palette.background.default, 0.5),
      }}
    >
      {/* User Profile */}
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: alpha(theme.palette.action.hover, 0.5),
          },
        }}
        onClick={handleProfileClick}
      >
        {loadingUser || !user ? (
          <Avatar sx={{ width: 40, height: 40, bgcolor: 'grey.400' }} />
        ) : user.picture ? (
          <Avatar src={user.picture} alt={user.name || user.email} sx={{ width: 40, height: 40 }} />
        ) : (
          <Avatar alt={user.name || user.email} sx={{ width: 40, height: 40, bgcolor: 'primary.main' }}>
            {(user.name || user.email)[0].toUpperCase()}
          </Avatar>
        )}

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {loadingUser || !user ? 'Loading...' : user.name || user.email}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {loadingUser || !user ? '' : user.email}
          </Typography>
        </Box>

        <Chip
          label={getPlanLabel()}
          size="small"
          color={getPlanColor()}
          variant={user?.plan === 'free' ? 'outlined' : 'filled'}
          sx={{
            height: 20,
            fontSize: '0.625rem',
            '& .MuiChip-label': {
              px: 0.5,
            },
          }}
        />
      </Box>

      {/* Plan Upgrade CTA */}
      {user?.plan === 'free' && !loadingUser && (
        <Box sx={{ px: 2, pb: 2 }}>
          <Button
            variant="contained"
            startIcon={<UpgradeIcon />}
            onClick={handleViewPlans}
            fullWidth
            size="small"
            sx={{
              textTransform: 'none',
              fontSize: '0.75rem',
              height: 32,
            }}
          >
            Upgrade to Pro
          </Button>
        </Box>
      )}

      {/* Profile Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        PaperProps={{
          sx: {
            minWidth: 200,
            mt: 1,
          },
        }}
      >
        <MenuItem onClick={handleSettings}>
          <ListItemIcon>
            <SettingsIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Settings</ListItemText>
        </MenuItem>

        <MenuItem>
          <ListItemIcon>
            <KeyboardIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Keyboard Shortcuts</ListItemText>
        </MenuItem>

        <MenuItem>
          <ListItemIcon>
            <HelpIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Help & Support</ListItemText>
        </MenuItem>

        <MenuItem>
          <ListItemIcon>
            <InfoIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>About</ListItemText>
        </MenuItem>

        <Divider />

        <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
          <ListItemIcon sx={{ color: 'error.main' }}>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Sign Out</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default BottomSection; 